import type { VoiceCallStatus } from '@/lib/twilio-voice';
import type { DialerLiveFanoutState } from './dialer-live-fanout';

export type DialerLiveTransportStatus = {
  status: 'idle' | 'connecting' | 'live' | 'retrying';
  label: 'Polling' | 'Push starting' | 'Push live' | 'Polling fallback';
  detail: string;
};

type ResolveDialerLiveTransportStatusInput = {
  callStatus: VoiceCallStatus;
  hasActiveCall: boolean;
  recentRealtimeTail: boolean;
  liveFanoutState: DialerLiveFanoutState;
};

export function resolveDialerLiveTransportStatus(
  input: ResolveDialerLiveTransportStatusInput,
): DialerLiveTransportStatus {
  const isRealtimeVoiceWindow =
    input.callStatus === 'calling' ||
    input.callStatus === 'ringing' ||
    (input.callStatus === 'in-progress' && input.hasActiveCall) ||
    input.recentRealtimeTail;

  if (!isRealtimeVoiceWindow) {
    return {
      status: 'idle',
      label: 'Polling',
      detail: 'Polling refresh remains active outside live voice sessions.',
    };
  }

  if (input.liveFanoutState === 'live') {
    return {
      status: 'live',
      label: 'Push live',
      detail: 'Live invalidation push is connected. Polling remains available as a fallback path.',
    };
  }

  if (input.liveFanoutState === 'connecting') {
    return {
      status: 'connecting',
      label: 'Push starting',
      detail:
        'Dialer is establishing live transcript push. Polling remains active until connected.',
    };
  }

  return {
    status: 'retrying',
    label: 'Polling fallback',
    detail:
      'Live push is unavailable or reconnecting. Polling keeps transcript and call history fresh.',
  };
}
